import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import {
  FiArrowDown,
  FiArrowLeft,
  FiArrowRight,
  FiArrowUpRight,
  FiCheck,
  FiChevronDown,
  FiColumns,
  FiExternalLink,
  FiFilter,
  FiInbox,
  FiList,
  FiMapPin,
  FiMoreHorizontal,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiTrash2,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import { useProjectCandidates } from '../../utils/useProjectCandidates';
import { handleViewsData } from '../../redux/common/commonSlice';
import { views } from '../../constants/views';
import { routesPath } from '../../constants/routesPath';
import {
  candidateDetails,
  candidateName,
  filterCandidates,
  isBinned,
} from './projectCandidateView';
import styles from './projectWorkspace.module.scss';
import { RemoveCandidatesDialog } from './RemoveCandidatesDialog';

const CandidateAvatar = ({ candidate }) => {
  const name = candidateName(candidate);
  const [failed, setFailed] = useState(false);
  return (
    <span className={styles.avatar} aria-hidden="true">
      {name
        .split(' ')
        .slice(0, 2)
        .map((part) => part[0])
        .join('')}
      {candidate.user?.avatar && !failed && (
        <img src={candidate.user.avatar} alt="" loading="lazy" onError={() => setFailed(true)} />
      )}
    </span>
  );
};

const CandidateMenu = ({
  candidate,
  stages,
  disabled,
  onMove,
  onBin,
  onRestore,
  onRemove,
  onProfile,
}) => {
  const menu = useRef(null);
  const [position, setPosition] = useState({});
  useEffect(() => {
    const close = (event) => {
      if (menu.current && !menu.current.contains(event.target)) menu.current.open = false;
      if (event.key === 'Escape' && menu.current?.open) {
        menu.current.open = false;
        menu.current.querySelector('summary')?.focus();
      }
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', close);
    };
  }, []);
  const perform = (action) => {
    menu.current.open = false;
    menu.current.querySelector('summary')?.focus();
    action();
  };
  return (
    <details
      className={styles.candidateMenu}
      ref={menu}
      onToggle={(event) => {
        if (event.currentTarget.open) {
          const rect = event.currentTarget.querySelector('summary').getBoundingClientRect();
          const height = Math.min(350, window.innerHeight - 24);
          setPosition({
            left: Math.max(12, Math.min(rect.right - 215, window.innerWidth - 227)),
            top: Math.max(12, Math.min(rect.bottom + 6, window.innerHeight - height - 12)),
            maxHeight: height,
          });
        }
      }}
    >
      <summary aria-label={`Actions for ${candidateName(candidate)}`}>
        <FiMoreHorizontal />
      </summary>
      <div className={styles.menuPanel} style={position}>
        <button type="button" onClick={() => perform(onProfile)}>
          <FiExternalLink /> View profile
        </button>
        <span className={styles.menuLabel}>Move to stage</span>
        {stages.map((stage) => {
          const isCurrent =
            !isBinned(candidate) && String(stage.id) === String(candidate.pipeline_stage_id);
          return isCurrent ? (
            <div key={stage.id} className={styles.currentStage}>
              <span className={styles.stageDot} />
              {stage.title}
              <FiCheck aria-label="Current stage" />
            </div>
          ) : (
            <button
              key={stage.id}
              type="button"
              disabled={disabled}
              onClick={() => perform(() => onMove(stage.id))}
            >
              <span className={styles.stageDot} />
              {stage.title}
            </button>
          );
        })}
        <div className={styles.menuDivider} />
        {isBinned(candidate) ? (
          <>
            <button type="button" disabled={disabled} onClick={() => perform(onRestore)}>
              <FiRefreshCw /> Restore candidate
            </button>
            <button
              type="button"
              className={styles.dangerText}
              disabled={disabled}
              onClick={() => perform(onRemove)}
            >
              <FiTrash2 /> Remove from project
            </button>
          </>
        ) : (
          <button type="button" disabled={disabled} onClick={() => perform(onBin)}>
            <FiInbox /> Move to bin
          </button>
        )}
      </div>
    </details>
  );
};

const EmptyState = ({ icon: Icon = FiUsers, title, children, action }) => (
  <div className={styles.emptyState}>
    <span className={styles.emptyIcon}>
      <Icon />
    </span>
    <h3>{title}</h3>
    <p>{children}</p>
    {action}
  </div>
);

export const ProjectRecruitingView = ({ mode = 'board', initialBin = false }) => {
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const {
    projectData,
    candidates = [],
    isLoadingProject,
    errorProject,
    refetch,
    isSaving,
    selectedIds,
    toggleSelected,
    selectIds,
    clearSelection,
    move,
    setBinned,
    remove,
  } = useProjectCandidates(projectId);
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const [query, setQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [sort, setSort] = useState('name');
  const [showBin, setShowBin] = useState(initialBin);
  const project = projectData?.data;
  const stages = useMemo(
    () =>
      [...(project?.pipeline?.pipeline_stages || [])].sort(
        (a, b) => Number(a.sort_order) - Number(b.sort_order) || Number(a.id) - Number(b.id),
      ),
    [project],
  );
  const visible = filterCandidates(candidates, { query, stage: stageFilter, bin: showBin, sort });
  const activeCount = candidates.filter((row) => !isBinned(row)).length;
  const binCount = candidates.filter(isBinned).length;
  const selected = visible.filter((row) => selectedIds.includes(row.id));
  const allSelected = visible.length > 0 && selected.length === visible.length;
  const displayedStages = stages.filter(
    (stage) => !stageFilter || String(stage.id) === stageFilter,
  );
  const stageIndex = (id) =>
    Math.max(
      0,
      stages.findIndex((stage) => String(stage.id) === String(id)),
    ) % 5;
  const selectVisible = () => selectIds(allSelected ? [] : visible.map((row) => row.id));

  useEffect(() => () => dispatch(handleViewsData(null)), [dispatch]);
  useEffect(() => {
    setQuery('');
    setStageFilter('');
    setShowBin(initialBin);
  }, [projectId, initialBin]);

  const openCandidate = (candidate) =>
    dispatch(
      handleViewsData({
        talent: { user: candidate.user },
        currentView: views.talentProfile,
      }),
    );
  const changeBin = () => {
    setShowBin((value) => !value);
    clearSelection();
  };
  const menuFor = (candidate) => (
    <CandidateMenu
      candidate={candidate}
      stages={stages}
      disabled={isSaving}
      onMove={(stageId) => move([candidate.id], stageId)}
      onBin={() => setBinned([candidate.id], 1)}
      onRestore={() => setBinned([candidate.id], 0)}
      onRemove={() => setPendingRemoval([candidate.id])}
      onProfile={() => openCandidate(candidate)}
    />
  );
  const dragEnd = ({ source, destination, draggableId }) => {
    if (!destination || isSaving || source.droppableId === destination.droppableId) return;
    const candidate = candidates.find((row) => String(row.id) === draggableId);
    const stage = stages.find((item) => String(item.id) === destination.droppableId);
    if (candidate && stage) move([candidate.id], stage.id);
  };

  if (isLoadingProject && !project)
    return (
      <div className={styles.loading} role="status">
        <span className={styles.spinner} /> Loading your candidates…
      </div>
    );
  if (errorProject || !project)
    return (
      <EmptyState
        icon={FiRefreshCw}
        title="We couldn’t load this project"
        action={
          <button className={styles.secondaryButton} onClick={refetch} type="button">
            <FiRefreshCw /> Try again
          </button>
        }
      >
        Please try again to load the latest candidate information.
      </EmptyState>
    );

  return (
    <section
      className={styles.content}
      aria-label={
        showBin ? 'Candidate bin' : mode === 'board' ? 'Hiring pipeline' : 'Project candidates'
      }
    >
      {pendingRemoval && (
        <RemoveCandidatesDialog
          count={pendingRemoval.length}
          onCancel={() => setPendingRemoval(null)}
          onConfirm={() => {
            remove(pendingRemoval, { confirmed: true });
            setPendingRemoval(null);
          }}
        />
      )}
      <div className={styles.sectionHeading}>
        <div>
          <div className={styles.sectionTitle}>
            <h2>
              {showBin ? 'Candidate bin' : mode === 'board' ? 'Hiring pipeline' : 'Candidates'}
            </h2>
            <span className={styles.countPill}>{showBin ? binCount : activeCount}</span>
          </div>
          <p>
            {showBin
              ? 'Candidates here are out of the active pipeline. Restore them whenever you need.'
              : mode === 'board'
              ? 'A clear view of every candidate and their next step.'
              : 'The people in your pipeline, with the details you need to make progress.'}
          </p>
        </div>
        <button
          type="button"
          onClick={changeBin}
          aria-pressed={showBin}
          className={showBin ? styles.secondaryButton : styles.binButton}
        >
          {showBin ? (
            <>
              <FiArrowLeft /> Back to {mode === 'board' ? 'pipeline' : 'candidates'}
            </>
          ) : (
            <>
              <FiInbox /> Candidate bin <span>{binCount}</span>
            </>
          )}
        </button>
      </div>

      <div className={styles.toolbar}>
        <label className={styles.search}>
          <FiSearch />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search candidates…"
            aria-label="Search candidates"
          />
          {query && (
            <button type="button" onClick={() => setQuery('')} aria-label="Clear search">
              <FiX />
            </button>
          )}
        </label>
        <label className={styles.filter}>
          <FiFilter />
          <span className={styles.srOnly}>Filter by stage</span>
          <select
            aria-label="Filter by stage"
            value={stageFilter}
            onChange={(event) => {
              setStageFilter(event.target.value);
              clearSelection();
            }}
          >
            <option value="">All stages</option>
            {stages.map((stage) => (
              <option key={stage.id} value={stage.id}>
                {stage.title}
              </option>
            ))}
          </select>
          <FiChevronDown />
        </label>
        <label className={styles.filter}>
          <FiArrowDown />
          <span className={styles.srOnly}>Sort candidates</span>
          <select
            aria-label="Sort candidates"
            value={sort}
            onChange={(event) => setSort(event.target.value)}
          >
            <option value="name">Name: A–Z</option>
            <option value="name-desc">Name: Z–A</option>
          </select>
          <FiChevronDown />
        </label>
        <span className={styles.toolbarStatus} role="status">
          {isSaving ? (
            <>
              <span className={styles.spinner} /> Updating…
            </>
          ) : (
            <>
              <FiCheck /> Up to date
            </>
          )}
        </span>
      </div>

      {selected.length > 0 && (
        <div className={styles.selectionBar} role="region" aria-label="Selected candidate actions">
          <button
            type="button"
            className={styles.clearSelection}
            onClick={clearSelection}
            aria-label="Clear selection"
          >
            <FiX />
          </button>
          <strong>{selected.length} selected</strong>
          <label className={styles.moveSelect}>
            <FiArrowRight />
            <select
              aria-label="Move selected candidates to stage"
              value=""
              disabled={isSaving}
              onChange={(event) => {
                if (event.target.value)
                  move(
                    selected.map((row) => row.id),
                    Number(event.target.value),
                  );
              }}
            >
              <option value="">Move to stage</option>
              {stages.map((stage) => (
                <option key={stage.id} value={stage.id}>
                  {stage.title}
                </option>
              ))}
            </select>
            <FiChevronDown />
          </label>
          <button
            type="button"
            disabled={isSaving}
            onClick={() =>
              setBinned(
                selected.map((row) => row.id),
                showBin ? 0 : 1,
              )
            }
          >
            {showBin ? <FiRefreshCw /> : <FiInbox />}
            {showBin ? 'Restore candidates' : 'Move to bin'}
          </button>
          {showBin && (
            <button
              type="button"
              disabled={isSaving}
              onClick={() => setPendingRemoval(selected.map((row) => row.id))}
            >
              <FiTrash2 /> Remove from project
            </button>
          )}
        </div>
      )}

      {!project.pipeline && mode === 'board' && !showBin ? (
        <EmptyState
          icon={FiColumns}
          title="Give your hiring process a home"
          action={
            <Link
              className={styles.primaryButton}
              to={routesPath.updateProject(project.client_id, project.id)}
            >
              <FiPlus /> Choose a pipeline
            </Link>
          }
        >
          Choose a pipeline in project settings to start organising your candidates into stages.
        </EmptyState>
      ) : visible.length === 0 && (query || showBin || mode === 'list') ? (
        <EmptyState
          icon={showBin ? FiInbox : FiSearch}
          title={
            query || stageFilter
              ? 'No candidates match your filters'
              : showBin
              ? 'Your candidate bin is empty'
              : 'Your next great hire starts here'
          }
          action={
            query || stageFilter ? (
              <button
                className={styles.secondaryButton}
                type="button"
                onClick={() => {
                  setQuery('');
                  setStageFilter('');
                }}
              >
                Clear filters
              </button>
            ) : !showBin ? (
              <Link className={styles.primaryButton} to={routesPath.searchCandidates()}>
                <FiPlus /> Find candidates
              </Link>
            ) : null
          }
        >
          {query || stageFilter
            ? 'Try another name, job title, company, or stage.'
            : showBin
            ? 'Candidates moved out of your pipeline will appear here.'
            : 'Find talent and add them to this project to build your shortlist.'}
        </EmptyState>
      ) : mode === 'board' && !showBin ? (
        <>
          <div className={styles.boardHint}>
            <span>
              <FiColumns /> {stages.length} stages <span className={styles.hintDot}>·</span> Drag
              cards to move candidates
            </span>
            <button type="button" onClick={selectVisible} disabled={!visible.length || isSaving}>
              {allSelected ? 'Deselect all' : 'Select all candidates'}
            </button>
          </div>
          <DragDropContext onDragEnd={dragEnd}>
            <div className={styles.board}>
              {displayedStages.map((stage) => {
                const rows = visible.filter(
                  (candidate) => String(candidate.pipeline_stage_id) === String(stage.id),
                );
                const total = candidates.filter(
                  (candidate) =>
                    !isBinned(candidate) &&
                    String(candidate.pipeline_stage_id) === String(stage.id),
                ).length;
                return (
                  <section
                    className={styles.column}
                    key={stage.id}
                    data-tone={stageIndex(stage.id)}
                    aria-label={`${stage.title}, ${total} ${
                      total === 1 ? 'candidate' : 'candidates'
                    }`}
                  >
                    <div className={styles.columnHeading}>
                      <span className={styles.stageDot} />
                      <h3>{stage.title}</h3>
                      <span className={styles.columnCount}>{total}</span>
                    </div>
                    <div className={styles.stageProgress}>
                      <span
                        style={{ width: `${activeCount ? (total / activeCount) * 100 : 0}%` }}
                      />
                    </div>
                    <Droppable droppableId={String(stage.id)} isDropDisabled={isSaving}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`${styles.columnBody} ${
                            snapshot.isDraggingOver ? styles.dragOver : ''
                          }`}
                        >
                          {rows.map((candidate, index) => {
                            const details = candidateDetails(candidate);
                            return (
                              <Draggable
                                key={candidate.id}
                                draggableId={String(candidate.id)}
                                index={index}
                                isDragDisabled={isSaving}
                              >
                                {(drag, dragging) => (
                                  <article
                                    ref={drag.innerRef}
                                    {...drag.draggableProps}
                                    {...drag.dragHandleProps}
                                    className={`${styles.candidateCard} ${
                                      selectedIds.includes(candidate.id) ? styles.selectedCard : ''
                                    } ${dragging.isDragging ? styles.dragging : ''}`}
                                    aria-label={candidateName(candidate)}
                                    role="button"
                                    tabIndex={0}
                                    onClick={(event) => {
                                      if (
                                        event.defaultPrevented ||
                                        event.target.closest(
                                          'button, input, a, summary, details, select',
                                        )
                                      )
                                        return;
                                      openCandidate(candidate);
                                    }}
                                    onKeyDown={(event) => {
                                      if (
                                        event.key === 'Enter' &&
                                        event.target === event.currentTarget
                                      ) {
                                        event.preventDefault();
                                        openCandidate(candidate);
                                      }
                                    }}
                                  >
                                    <div className={styles.cardTop}>
                                      <input
                                        className={styles.checkbox}
                                        type="checkbox"
                                        aria-label={`Select ${candidateName(candidate)}`}
                                        checked={selectedIds.includes(candidate.id)}
                                        disabled={isSaving}
                                        onChange={() => toggleSelected(candidate.id)}
                                      />
                                      {menuFor(candidate)}
                                    </div>
                                    <div className={styles.cardPerson}>
                                      <CandidateAvatar candidate={candidate} />
                                      <span>
                                        <strong title={candidateName(candidate)}>
                                          {candidateName(candidate)}
                                        </strong>
                                        <span title={details.title}>
                                          {details.title || 'No job title added'}
                                        </span>
                                      </span>
                                    </div>
                                    <div className={styles.cardCompany} title={details.company}>
                                      {details.company || 'Company not added'}
                                    </div>
                                    <div className={styles.cardFooter}>
                                      <span title={details.location}>
                                        <FiMapPin />
                                        {details.location || 'Location not added'}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => openCandidate(candidate)}
                                        aria-label={`View ${candidateName(candidate)}’s profile`}
                                      >
                                        <FiArrowUpRight />
                                      </button>
                                    </div>
                                  </article>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                          {rows.length === 0 && (
                            <div className={styles.emptyColumn}>
                              <span aria-hidden="true">
                                <FiInbox />
                              </span>
                              <strong>
                                {query ? 'No matches in this stage' : 'Ready for the next step'}
                              </strong>
                              <p>
                                {query
                                  ? 'Try a different search.'
                                  : 'Drag a candidate here or use “Move to stage”.'}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </Droppable>
                  </section>
                );
              })}
            </div>
          </DragDropContext>
          <div className={styles.bottomNote}>
            <FiUsers />
            <span>
              {visible.length} active {visible.length === 1 ? 'candidate' : 'candidates'}
              {query ? ' matching your search' : ' in this pipeline'}
            </span>
            <span>Tip: select cards to move several at once.</span>
          </div>
        </>
      ) : (
        <>
          <div className={styles.mobileListActions}>
            <span>{visible.length} candidates</span>
            <button type="button" onClick={selectVisible} disabled={isSaving}>
              {allSelected ? 'Deselect all' : 'Select all'}
            </button>
          </div>
          <div className={styles.tableShell}>
            <table className={styles.table}>
              <caption className={styles.srOnly}>
                {showBin ? 'Binned project candidates' : 'Project candidates'}
              </caption>
              <thead>
                <tr>
                  <th className={styles.selectionCell}>
                    <input
                      type="checkbox"
                      className={styles.checkbox}
                      checked={allSelected}
                      aria-label="Select all visible candidates"
                      disabled={isSaving}
                      onChange={selectVisible}
                    />
                  </th>
                  <th>Candidate</th>
                  <th>Current company</th>
                  <th>Location</th>
                  <th>Pipeline stage</th>
                  <th>
                    <span className={styles.srOnly}>Candidate actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.map((candidate) => {
                  const details = candidateDetails(candidate);
                  const stage = stages.find(
                    (item) => String(item.id) === String(candidate.pipeline_stage_id),
                  );
                  return (
                    <tr
                      key={candidate.id}
                      className={
                        selectedIds.includes(candidate.id) ? styles.selectedRow : undefined
                      }
                    >
                      <td className={styles.selectionCell}>
                        <input
                          className={styles.checkbox}
                          type="checkbox"
                          checked={selectedIds.includes(candidate.id)}
                          aria-label={`Select ${candidateName(candidate)}`}
                          disabled={isSaving}
                          onChange={() => toggleSelected(candidate.id)}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className={styles.tablePerson}
                          onClick={() => openCandidate(candidate)}
                        >
                          <CandidateAvatar candidate={candidate} />
                          <span>
                            <strong>{candidateName(candidate)}</strong>
                            <span title={details.title}>
                              {details.title || 'No job title added'}
                            </span>
                          </span>
                        </button>
                      </td>
                      <td>
                        <span className={styles.companyText}>{details.company || '—'}</span>
                      </td>
                      <td>
                        <span className={styles.locationText}>{details.location || '—'}</span>
                      </td>
                      <td>
                        <span
                          className={styles.stageBadge}
                          data-tone={stageIndex(candidate.pipeline_stage_id)}
                        >
                          <span className={styles.stageDot} />
                          {stage?.title || 'Unassigned'}
                        </span>
                      </td>
                      <td>
                        <div className={styles.rowActions}>
                          {showBin ? (
                            <button
                              className={styles.restoreButton}
                              disabled={isSaving}
                              type="button"
                              onClick={() => setBinned([candidate.id], 0)}
                            >
                              <FiRefreshCw /> Restore
                            </button>
                          ) : null}
                          {menuFor(candidate)}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className={styles.bottomNote}>
            <span>
              Showing {visible.length} of {showBin ? binCount : activeCount}{' '}
              {showBin ? 'binned' : 'active'} candidates
            </span>
            <span>
              <FiList /> All candidate details in one place
            </span>
          </div>
        </>
      )}
    </section>
  );
};
